import { Command } from "./Command";
import { HistoryEventHandler } from "../history";

/**
* History command that can contain/execute multiple other commands.
* @implements {module:history.HistoryCommand}
*/
export class BatchCommand extends Command {
    stack: Command[];

    /**
    * @param {string} [text] - An optional string visible to user related to this change
    */
    constructor(text?: string) {
        super();

        this.text = text || 'Batch Command';
        this.stack = [];
    }

    /**
    * Runs "apply" on all subcommands.
    * @param {module:history.HistoryEventHandler} handler
    * @fires module:history~Command#event:history
    * @returns {void}
    */
    apply(handler: HistoryEventHandler) {
        super.apply(handler, () => {
            this.stack.forEach((stackItem) => {
                console.assert(stackItem, 'stack item should not be null');
                stackItem && stackItem.apply(handler);
            });
        });
    }

    /**
    * Runs "unapply" on all subcommands.
    * @param {module:history.HistoryEventHandler} handler
    * @fires module:history~Command#event:history
    * @returns {void}
    */
    unapply(handler: HistoryEventHandler) {
        super.unapply(handler, () => {
            this.stack.slice().reverse().forEach((stackItem) => {
                console.assert(stackItem, 'stack item should not be null');
                stackItem && stackItem.unapply(handler);
            });
        });
    }

    /**
    * Iterate through all our subcommands.
    * @returns {Element[]} All the elements we are changing
    */
    elements() {
        const elems = [];
        let cmd = this.stack.length;

        while (cmd--) {
            if (!this.stack[cmd]) {
                continue;
            }

            const thisElems = this.stack[cmd].elements();

            let elem = thisElems.length;
            while (elem--) {
                if (!elems.includes(thisElems[elem])) {
                    elems.push(thisElems[elem]);
                }
            }
        }
        return elems;
    }

    /**
    * Adds a given command to the history stack.
    * @param {Command} cmd - The undo command object to add
    * @returns {void}
    */
    addSubCommand(cmd: Command) {
        console.assert(cmd !== null, 'cmd should not be null');

        if (this.stack.length > 0 && this.stack[this.stack.length - 1].mergeHistory(cmd, "Multiple changes")) {
            cmd = null;
        }

        if (cmd && !cmd.isNoOp()) {
            this.stack.push(cmd);
        }
    }

    /**
    * @returns {boolean} Indicates whether or not the batch command is empty
    */
    isEmpty() {
        return !this.stack.length;
    }

    isNoOp() {
        return this.isEmpty();
    }

    canUnpack() {
        return this.stack.length == 1;
    }
}
